import NProgress from "nprogress";
import { Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  createBrowserRouter,
  Outlet,
  type RouteObject,
  useLocation,
  useNavigation,
} from "react-router";
import { LegacyPrefixRedirect } from "~/components/LegacyPrefixRedirect";
import { PageErrorFallback } from "~/components/ui/PageErrorFallback";
import { InnerProviders } from "./AppProviders";
import { legacyRedirectRoutes } from "./legacyRedirects";
import NotFoundOrErrorPage from "./pages/_not-found";
import { reloadOnChunkError } from "./utils/chunkReload";

/**
 * Root layout - wraps all routes.
 * - InnerProviders: CommandBar, Analytics, PostHog (need Router context)
 * - NProgress: loading bar on navigation (starts when lazy route begins loading)
 */
function RootLayout() {
  const navigation = useNavigation();
  const location = useLocation();

  useEffect(() => {
    NProgress.configure({ showSpinner: false });
  }, []);

  useEffect(() => {
    if (navigation.state === "loading") {
      NProgress.start();
    } else {
      NProgress.done();
    }
  }, [navigation.state]);

  return (
    <InnerProviders>
      <ErrorBoundary
        FallbackComponent={PageErrorFallback}
        resetKeys={[location.pathname]}
      >
        <Suspense>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </InnerProviders>
  );
}

/**
 * Helper: wraps a dynamic import() into the shape React Router's `lazy` expects.
 * React Router's lazy keeps the OLD route visible while the new module loads,
 * eliminating the gray flash that React.lazy + Suspense causes.
 */
const page = (importFn: () => Promise<{ default: React.ComponentType }>) => ({
  lazy: () =>
    importFn()
      .then((m) => ({ Component: m.default }))
      .catch((err: unknown) => {
        // Stale route chunk after a deploy → reload once to pick up the new
        // hashes. Non-chunk errors fall through to the error boundary.
        reloadOnChunkError(err);
        throw err;
      }),
});

const routes: RouteObject[] = [
  // Auth (public)
  { path: "/auth/signin", ...page(() => import("./pages/auth/signin")) },
  { path: "/auth/signup", ...page(() => import("./pages/auth/signup")) },
  {
    path: "/auth/forgot-password",
    ...page(() => import("./pages/auth/forgot-password")),
  },
  {
    path: "/auth/reset-password",
    ...page(() => import("./pages/auth/reset-password")),
  },
  // The email-verification magic link lands here; it renders only (D01).
  {
    path: "/auth/verify-email",
    ...page(() => import("./pages/auth/verify-email")),
  },
  { path: "/auth/error", ...page(() => import("./pages/auth/error")) },
  // Join before create (ADR-117 §6): a new account passes through here on its
  // way to making an organization. Renders nothing until D12 fills it.
  { path: "/auth/join", ...page(() => import("./pages/auth/join")) },

  // Top-level pages
  { path: "/", ...page(() => import("./pages/index")) },
  { path: "/authorize", ...page(() => import("./pages/authorize")) },
  { path: "/admin/*", ...page(() => import("./pages/admin/index")) },
  { path: "/invite/accept", ...page(() => import("./pages/invite/accept")) },
  { path: "/mcp/authorize", ...page(() => import("./pages/mcp/authorize")) },
  { path: "/share/:id", ...page(() => import("./pages/share/[id]")) },
  // Public — no auth required; token in query-string is the authorisation
  { path: "/unsubscribe", ...page(() => import("./pages/unsubscribe")) },

  // Onboarding
  { path: "/onboarding", ...page(() => import("./pages/onboarding")) },
  {
    path: "/onboarding/:team/project",
    ...page(() => import("./pages/onboarding/[team]/project")),
  },
  {
    path: "/onboarding/product",
    ...page(() => import("./pages/onboarding/product/index")),
  },
  {
    path: "/onboarding/welcome",
    ...page(() => import("./pages/onboarding/welcome")),
  },

  // Settings, wrapped in the same Langy layout as the project routes
  // (keyed by the AMBIENT project), so the panel survives hopping between
  // a project page and settings instead of vanishing.
  {
    ...page(() => import("./features/langy/ProjectLangyLayout")),
    children: [
      { path: "/settings", ...page(() => import("./pages/settings")) },
      {
        path: "/settings/role-bindings",
        ...page(() => import("./pages/settings/role-bindings")),
      },
      {
        path: "/settings/annotation-scores",
        ...page(() => import("./pages/settings/annotation-scores")),
      },
      {
        path: "/settings/data-retention",
        ...page(() => import("./pages/settings/data-retention")),
      },
      {
        path: "/settings/integrations",
        ...page(() => import("./pages/settings/integrations")),
      },
      {
        path: "/settings/data-privacy",
        ...page(() => import("./pages/settings/data-privacy")),
      },
      {
        path: "/settings/audit-log",
        ...page(() => import("./pages/settings/audit-log")),
      },
      {
        path: "/settings/authentication",
        ...page(() => import("./pages/settings/authentication")),
      },
      {
        path: "/settings/groups",
        ...page(() => import("./pages/settings/groups")),
      },
      {
        path: "/settings/license",
        ...page(() => import("./pages/settings/license")),
      },
      {
        path: "/settings/members",
        ...page(() => import("./pages/settings/members")),
      },
      {
        path: "/settings/model-costs",
        ...page(() => import("./pages/settings/model-costs")),
      },
      {
        path: "/settings/model-providers",
        ...page(() => import("./pages/settings/model-providers")),
      },
      {
        path: "/settings/plans",
        ...page(() => import("./pages/settings/plans")),
      },
      {
        path: "/settings/roles",
        ...page(() => import("./pages/settings/roles")),
      },
      {
        path: "/settings/api-keys",
        ...page(() => import("./pages/settings/api-keys")),
      },
      {
        path: "/settings/scim",
        ...page(() => import("./pages/settings/scim")),
      },
      {
        path: "/settings/secrets",
        ...page(() => import("./pages/settings/secrets")),
      },
      {
        path: "/settings/subscription",
        ...page(() => import("./pages/settings/subscription")),
      },
      {
        path: "/settings/teams",
        ...page(() => import("./pages/settings/teams")),
      },
      {
        path: "/settings/teams/:team",
        ...page(() => import("./pages/settings/teams/[team]")),
      },
      {
        path: "/settings/topic-clustering",
        ...page(() => import("./pages/settings/topic-clustering")),
      },
      {
        path: "/settings/usage",
        ...page(() => import("./pages/settings/usage")),
      },
      {
        path: "/settings/email-suppressions",
        ...page(() => import("./pages/settings/email-suppressions")),
      },
      {
        // Governance home (admin oversight dashboard). The whole family
        // lives at the top level: it is org-scoped, not a settings page.
        path: "/governance",
        ...page(() => import("./pages/governance/index")),
      },
      {
        // The inventory: a tabbed shell — Catalog (the tool-tiles editor)
        // and Sources (the ingestion-sources table). The retired
        // /governance/{catalog,ingestion-sources,tool-catalog} addresses
        // redirect here via legacyRedirectRoutes.
        path: "/governance/inventory",
        ...page(() => import("@ee/governance/dashboard/pages/inventory")),
      },
      {
        path: "/governance/inventory/:id",
        ...page(
          () =>
            import("@ee/governance/dashboard/pages/ingestion-source-detail"),
        ),
      },
      {
        path: "/governance/anomaly-rules",
        ...page(() => import("@ee/governance/dashboard/pages/anomaly-rules")),
      },
      {
        path: "/governance/people",
        ...page(() => import("./pages/governance/people")),
      },
      {
        // Behind release_ui_governance_billed_cost_enabled (the pages
        // guard themselves); the nav items are filtered on the same flag.
        path: "/governance/costs",
        ...page(() => import("./pages/governance/costs")),
      },
      {
        path: "/governance/billed",
        ...page(() => import("./pages/governance/billed")),
      },
      {
        // The people page has been cost centers and then departments; old
        // bookmarks land on the newest name in one hop (the legacy
        // /settings/governance/cost-centers address chains through here,
        // and /governance/departments redirects via legacyRedirectRoutes).
        path: "/governance/cost-centers",
        element: (
          <LegacyPrefixRedirect
            from="/governance/cost-centers"
            to="/governance/people"
          />
        ),
      },
      {
        // View-all teams listing - bird's-eye `View all teams →` lands here.
        // 500-row paginated list with sort chips for spend / requests /
        // last-activity. Per-row click-through routes to the team detail
        // page below.
        path: "/governance/teams",
        ...page(() => import("./pages/governance/teams")),
      },
      {
        // Per-team detail - single-row scoped view of `spendByTeam` filtered
        // to the URL-encoded team id, four-stat KPI grid + breadcrumb back
        // to the listing. Detail-data depth (per-day trend, per-user
        // breakdown, model mix) defers to a follow-up.
        path: "/governance/teams/:id",
        ...page(() => import("./pages/governance/teams/[id]")),
      },
      {
        // View-all users listing - bird's-eye `View all users →` lands here.
        path: "/governance/users",
        ...page(() => import("./pages/governance/users")),
      },
      {
        // Per-user detail - single-row scoped view keyed off the
        // URL-encoded actor id (email / sub claim).
        path: "/governance/users/:id",
        ...page(() => import("./pages/governance/users/[id]")),
      },

      // Personal-scope governance routes (must precede the /:project catch-all
      // so "me" doesn't get treated as a project slug)
      {
        path: "/me",
        ...page(() => import("./pages/me/index")),
      },
      {
        path: "/me/configure",
        ...page(() => import("./pages/me/configure")),
      },
      {
        // The devices inventory moved into a tab of /me/configure, and this
        // path keeps resolving so old links do not dead-end. A page that
        // renders <Navigate>, not a `loader` redirect: loaders do not run on a
        // cold load of the SPA, which is exactly how a stale link arrives.
        path: "/me/devices",
        ...page(() => import("./pages/me/devices")),
      },
      {
        path: "/me/pull-requests",
        ...page(() => import("./pages/me/pull-requests")),
      },
      {
        path: "/me/sessions",
        ...page(() => import("./pages/me/sessions")),
      },
      {
        // Budget-increase request page that the CLI's `langwatch request-increase`
        // opens. The page file existed but routes.tsx is explicit (Vite migration)
        // - without this entry the URL 404'd, breaking the per-spec
        // budget-exceeded → request flow Ariana caught in dogfood.
        path: "/me/budget/request",
        ...page(() => import("./pages/me/budget/request")),
      },

      // CLI device-flow approval (RFC 8628 user-facing screen)
      {
        path: "/cli/auth",
        ...page(() => import("./pages/cli/auth")),
      },

      // AI Gateway: org-scoped admin pages live under /gateway/** at the top
      // level, like /governance. Every gateway resource (VirtualKey /
      // GatewayBudget / ModelProvider) is org-keyed by the schema, so the
      // chrome reflects that. Kept OUT of the project layout route below:
      // these are not /:project/* routes, so Langy must not mount on them.
      // The static /gateway segment always wins over the /:project catch-all,
      // and project slug minting refuses reserved top-level names.
      {
        path: "/gateway",
        ...page(() => import("./pages/gateway/index")),
      },
      {
        path: "/gateway/virtual-keys",
        ...page(() => import("./pages/gateway/virtual-keys")),
      },
      {
        path: "/gateway/virtual-keys/:id",
        ...page(() => import("./pages/gateway/virtual-keys/[id]")),
      },
      {
        path: "/gateway/budgets",
        ...page(() => import("./pages/gateway/budgets")),
      },
      {
        path: "/gateway/budgets/:id",
        ...page(() => import("./pages/gateway/budgets/[id]")),
      },
      {
        path: "/gateway/routing-policies",
        ...page(() => import("./pages/gateway/routing-policies")),
      },
      {
        path: "/gateway/usage",
        ...page(() => import("./pages/gateway/usage")),
      },
      {
        path: "/gateway/cache-rules",
        ...page(() => import("./pages/gateway/cache-rules")),
      },
      {
        path: "/gateway/guardrails",
        ...page(() => import("./pages/gateway/guardrails")),
      },
      {
        path: "/gateway/billing-events",
        ...page(() => import("./pages/gateway/billing-events")),
      },
      {
        path: "/gateway/webhooks",
        ...page(() => import("./pages/gateway/webhooks")),
      },
      ...legacyRedirectRoutes,
    ],
  },

  // Project routes — wrapped in a layout route that mounts Langy ONCE per
  // project, above the swapping page, so the panel + composer draft + any
  // in-flight response survive navigation between project pages. The layout
  // component is keyed by :project, so Langy resets on project switch. Loaded
  // lazily via page() so Langy's chat bundle stays out of the initial load.
  // See ProjectLangyLayout + specs/langy/langy-navigation-persistence.feature
  {
    ...page(() => import("./features/langy/ProjectLangyLayout")),
    children: [
      {
        path: "/:project",
        ...page(() => import("./pages/[project]/index")),
      },
      {
        path: "/:project/agents",
        ...page(() => import("./pages/[project]/agents")),
      },

      // Coding-agent activity, project scope
      {
        path: "/:project/sessions",
        ...page(() => import("./pages/[project]/sessions")),
      },
      {
        path: "/:project/pull-requests",
        ...page(() => import("./pages/[project]/pull-requests")),
      },

      {
        path: "/:project/automations",
        ...page(() => import("./pages/[project]/automations")),
      },
      {
        path: "/:project/automations/automations",
        ...page(() => import("./pages/[project]/automations/automations")),
      },
      {
        path: "/:project/automations/alerts",
        ...page(() => import("./pages/[project]/automations/alerts")),
      },
      {
        path: "/:project/automations/schedules",
        ...page(() => import("./pages/[project]/automations/schedules")),
      },
      {
        path: "/:project/automations/activity",
        ...page(() => import("./pages/[project]/automations/activity")),
      },
      {
        path: "/:project/datasets",
        ...page(() => import("./pages/[project]/datasets")),
      },
      {
        path: "/:project/datasets/:id",
        ...page(() => import("./pages/[project]/datasets/[id]")),
      },
      {
        path: "/:project/evaluators",
        ...page(() => import("./pages/[project]/evaluators")),
      },
      {
        path: "/:project/evaluations",
        ...page(() => import("./pages/[project]/evaluations")),
      },
      {
        path: "/:project/online-evaluations",
        ...page(() => import("./pages/[project]/online-evaluations")),
      },
      {
        path: "/:project/evaluations/new",
        ...page(() => import("./pages/[project]/evaluations/new")),
      },
      {
        path: "/:project/evaluations/new/choose",
        ...page(() => import("./pages/[project]/evaluations/new/choose")),
      },
      {
        path: "/:project/evaluations/wizard",
        ...page(() => import("./pages/[project]/evaluations/wizard")),
      },
      {
        path: "/:project/evaluations/wizard/:slug",
        ...page(() => import("./pages/[project]/evaluations/wizard/[slug]")),
      },
      {
        path: "/:project/evaluations/:id/edit",
        ...page(() => import("./pages/[project]/evaluations/[id]/edit")),
      },
      {
        path: "/:project/evaluations/:id/edit/choose",
        ...page(() => import("./pages/[project]/evaluations/[id]/edit/choose")),
      },
      {
        path: "/:project/traces",
        ...page(() => import("./pages/[project]/traces")),
      },
      {
        path: "/:project/traces/:trace",
        ...page(() => import("./pages/[project]/traces/[trace]")),
      },

      // Legacy /messages paths. The legacy Traces page is gone; these are
      // redirects only, so old bookmarks and notification links keep working.
      {
        path: "/:project/messages",
        ...page(() => import("./pages/[project]/messages/index")),
      },
      {
        path: "/:project/messages/:trace",
        ...page(() => import("./pages/[project]/messages/[trace]/index")),
      },
      {
        path: "/:project/messages/:trace/:openTab",
        ...page(
          () => import("./pages/[project]/messages/[trace]/[openTab]/index"),
        ),
      },
      {
        path: "/:project/messages/:trace/:openTab/:span",
        ...page(
          () => import("./pages/[project]/messages/[trace]/[openTab]/[span]"),
        ),
      },
      {
        path: "/:project/prompts",
        ...page(() => import("./pages/[project]/prompts")),
      },
      {
        path: "/:project/setup",
        ...page(() => import("./pages/[project]/setup")),
      },
      {
        path: "/:project/workflows",
        ...page(() => import("./pages/[project]/workflows")),
      },
      {
        path: "/:project/chat/:workflow",
        ...page(() => import("./pages/[project]/chat/[workflow]")),
      },
      {
        path: "/:project/studio/:workflow",
        ...page(() => import("./pages/[project]/studio/[workflow]")),
      },

      // Annotations
      {
        path: "/:project/annotations",
        ...page(() => import("./pages/[project]/annotations")),
      },
      {
        path: "/:project/annotations/all",
        ...page(() => import("./pages/[project]/annotations/all")),
      },
      {
        path: "/:project/annotations/me",
        ...page(() => import("./pages/[project]/annotations/me")),
      },
      {
        path: "/:project/annotations/my-queue",
        ...page(() => import("./pages/[project]/annotations/my-queue")),
      },
      {
        path: "/:project/annotations/:slug",
        ...page(() => import("./pages/[project]/annotations/[slug]")),
      },

      // Analytics
      {
        path: "/:project/analytics",
        ...page(() => import("./pages/[project]/analytics/index")),
      },
      {
        path: "/:project/analytics/evaluations",
        ...page(() => import("./pages/[project]/analytics/evaluations")),
      },
      {
        path: "/:project/analytics/metrics",
        ...page(() => import("./pages/[project]/analytics/metrics")),
      },
      {
        path: "/:project/analytics/reports",
        ...page(() => import("./pages/[project]/analytics/reports")),
      },
      {
        path: "/:project/analytics/topics",
        ...page(() => import("./pages/[project]/analytics/topics")),
      },
      {
        path: "/:project/analytics/users",
        ...page(() => import("./pages/[project]/analytics/users")),
      },
      {
        path: "/:project/analytics/query",
        ...page(() => import("./pages/[project]/analytics/query")),
      },
      {
        path: "/:project/analytics/custom",
        ...page(() => import("./pages/[project]/analytics/custom/index")),
      },
      {
        path: "/:project/analytics/custom/:id",
        ...page(() => import("./pages/[project]/analytics/custom/[id]")),
      },

      // Experiments
      {
        path: "/:project/experiments",
        ...page(() => import("./pages/[project]/experiments/index")),
      },
      {
        path: "/:project/experiments/workbench",
        ...page(() => import("./pages/[project]/experiments/workbench/index")),
      },
      {
        path: "/:project/experiments/workbench/:slug",
        ...page(() => import("./pages/[project]/experiments/workbench/[slug]")),
      },
      {
        path: "/:project/experiments/:experiment",
        ...page(() => import("./pages/[project]/experiments/[experiment]")),
      },

      // Agent Testing (catch-all, behind release_ui_agent_testing_v2_enabled)
      {
        path: "/:project/agent-testing",
        ...page(() => import("./pages/[project]/agent-testing/[[...path]]")),
      },
      {
        path: "/:project/agent-testing/*",
        ...page(() => import("./pages/[project]/agent-testing/[[...path]]")),
      },

      // Simulations (catch-all)
      {
        path: "/:project/simulations/scenarios",
        ...page(() => import("./pages/[project]/simulations/scenarios/index")),
      },
      {
        path: "/:project/simulations/*",
        ...page(() => import("./pages/[project]/simulations/[[...path]]")),
      },
      {
        path: "/:project/simulations",
        ...page(() => import("./pages/[project]/simulations/[[...path]]")),
      },
    ],
  },

  // Ops
  { path: "/ops", ...page(() => import("./pages/ops/index")) },
  { path: "/ops/queues", ...page(() => import("./pages/ops/queues")) },
  { path: "/ops/dejaview", ...page(() => import("./pages/ops/dejaview")) },
  { path: "/ops/scheduler", ...page(() => import("./pages/ops/scheduler")) },
  {
    path: "/ops/event-sourcing",
    ...page(() => import("./pages/ops/event-sourcing/index")),
  },
  {
    path: "/ops/event-sourcing/dead-letters",
    ...page(() => import("./pages/ops/event-sourcing/dead-letters")),
  },
  {
    path: "/ops/event-sourcing/processes",
    ...page(() => import("./pages/ops/event-sourcing/processes")),
  },
  {
    path: "/ops/event-sourcing/projections",
    ...page(() => import("./pages/ops/event-sourcing/projections")),
  },
  {
    path: "/ops/event-sourcing/subscribers",
    ...page(() => import("./pages/ops/event-sourcing/subscribers")),
  },
  {
    path: "/ops/event-sourcing/schedules",
    ...page(() => import("./pages/ops/event-sourcing/schedules")),
  },
  { path: "/ops/blobs", ...page(() => import("./pages/ops/blobs")) },
  {
    path: "/ops/feature-flags",
    ...page(() => import("./pages/ops/feature-flags")),
  },
  { path: "/ops/foundry", ...page(() => import("./pages/ops/foundry")) },
  {
    path: "/ops/migrations",
    ...page(() => import("./pages/ops/migrations")),
  },
  {
    path: "/ops/projections",
    ...page(() => import("./pages/ops/projections")),
  },
  {
    path: "/ops/projections/:runId",
    ...page(() => import("./pages/ops/projections/[runId]")),
  },
  {
    path: "/ops/backoffice",
    ...page(() => import("./pages/ops/backoffice")),
  },
  {
    path: "/ops/backoffice/bug-reports",
    ...page(() => import("./pages/ops/backoffice/bug-reports")),
  },
  {
    path: "/ops/backoffice/users",
    ...page(() => import("./pages/ops/backoffice/users")),
  },
  {
    path: "/ops/backoffice/organizations",
    ...page(() => import("./pages/ops/backoffice/organizations")),
  },
  {
    path: "/ops/backoffice/projects",
    ...page(() => import("./pages/ops/backoffice/projects")),
  },
  {
    path: "/ops/backoffice/subscriptions",
    ...page(() => import("./pages/ops/backoffice/subscriptions")),
  },
  {
    path: "/ops/backoffice/sso-connections",
    ...page(() => import("./pages/ops/backoffice/sso-connections")),
  },

  // @project redirect - Next.js parallel route that redirects /@project/path to /:project/path
  {
    path: "/@project/*",
    ...page(() => import("./pages/@project/[...path]/index")),
  },

  // Catch-all 404 - must stay last.
  {
    path: "*",
    ...page(() => import("./pages/not-found")),
  },
];

// React Router v7 expects a HydrateFallback when the root route is async; the
// app uses lazy() per-route and never blocks hydration, but RR still emits a
// console warning if no fallback is declared. An empty fragment is correct
// here - InnerProviders + Suspense already render the right shell once
// children resolve, and showing nothing for the few ms before that is the
// existing behaviour.
function HydrateFallback() {
  return null;
}

export const router = createBrowserRouter([
  {
    Component: RootLayout,
    HydrateFallback,
    // ErrorBoundary catches render + loader throws anywhere below this node
    // and renders the same fallback used by the catch-all 404 route.
    ErrorBoundary: NotFoundOrErrorPage,
    children: routes,
  },
]);
